import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Upload,
  Row,
  Col,
  Divider,
  notification,
  Space,
  Select,
  List,
  Spin,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { validateInvoices } from '../../../../../api/billing';
import toBase64 from '../../../../../utils/toBase64';
import { getCompanyIdApi } from '../../../../../api/company';
import { getAccessTokenApi } from '../../../../../api/auth';
import useDataGeneral from '../../../../../hooks/useDataGeneral';

export default function UploadSupplierBillings({
  setModalIsVisible,
  type,
  operationIds = [],
  setReloadOperations,
}) {
  const { Title, Text } = Typography;
  const companyId = getCompanyIdApi();
  const token = getAccessTokenApi();
  const { setStatusData } = useDataGeneral();

  const [currentStep, setCurrentStep] = useState(0);

  const [filesByOp, setFilesByOp] = useState(() =>
    (operationIds || []).reduce((acc, id) => {
      acc[id] = {
        xml: null,
        pdf: null,
        cfdi: null,
        xmlLoading: false,
        pdfLoading: false,
        cfdiLoading: false,
        xmlPercent: 0,
        pdfPercent: 0,
        cfdiPercent: 0,
      };
      return acc;
    }, {})
  );

  const [submitting, setSubmitting] = useState(false);
  const [resultOps, setResultOps] = useState([]);
  const [resultErrors, setResultErrors] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    setFilesByOp((prev) => {
      const next = { ...prev };
      (operationIds || []).forEach((id) => {
        if (!next[id]) {
          next[id] = {
            xml: null,
            pdf: null,
            cfdi: null,
            xmlLoading: false,
            pdfLoading: false,
            cfdiLoading: false,
            xmlPercent: 0,
            pdfPercent: 0,
            cfdiPercent: 0,
          };
        }
      });
      Object.keys(next).forEach((k) => {
        if (!operationIds.includes(k)) delete next[k];
      });
      return next;
    });
    if (!operationIds || operationIds.length === 0) setCurrentStep(0);
    else if (currentStep > operationIds.length - 1) setCurrentStep(0);
  }, [operationIds]);

  const currentId = operationIds[currentStep];
  const current = currentId ? (filesByOp[currentId] || {}) : {};

  const setFile = (opId, key, file) => {
    setFilesByOp((prev) => ({
      ...prev,
      [opId]: { ...prev[opId], [key]: file },
    }));
  };

  const setFlag = (opId, patch) => {
    setFilesByOp((prev) => ({
      ...prev,
      [opId]: { ...prev[opId], ...patch },
    }));
  };

  const buildFileListAny = (val, loading, percent) => {
    if (Array.isArray(val)) {
      if (val.length === 0) {
        return loading
          ? [
              {
                uid: 'temp-loading',
                name: 'Procesando…',
                status: 'uploading',
                percent: Math.max(1, Math.min(99, percent || 0)),
              },
            ]
          : [];
      }
      return val.slice(0, 3).map((file, idx) => ({
        uid: `${file.name}-${file.size || ''}-${idx}`,
        name: file.name,
        status: 'done',
        percent: 100,
      }));
    } else if (val) {
      return [
        {
          uid: `${val.name}-${val.size || ''}`,
          name: val.name,
          status: loading ? 'uploading' : 'done',
          percent: loading ? Math.max(1, Math.min(99, percent || 0)) : 100,
        },
      ];
    } else {
      return loading
        ? [
            {
              uid: 'temp-loading',
              name: 'Procesando…',
              status: 'uploading',
              percent: Math.max(1, Math.min(99, percent || 0)),
            },
          ]
        : [];
    }
  };

  const uploadProps = (opId, key, accept, loadingKey, percentKey, tipName, isMultiple = false) => ({
    accept,
    multiple: isMultiple,
    maxCount: isMultiple ? 3 : 1,
    showUploadList: { showRemoveIcon: true, showPreviewIcon: false, showDownloadIcon: false },
    fileList: buildFileListAny(
      filesByOp[opId]?.[key],
      filesByOp[opId]?.[loadingKey],
      filesByOp[opId]?.[percentKey]
    ),
    onRemove: (file) => {
      if (isMultiple) {
        setFilesByOp((prev) => {
          const arr = Array.isArray(prev[opId]?.cfdi) ? prev[opId].cfdi : [];
          const newArr = arr.filter((f) => f.name !== file.name);
          return { ...prev, [opId]: { ...prev[opId], cfdi: newArr } };
        });
      } else {
        setFile(opId, key, null);
        setFlag(opId, { [percentKey]: 0 });
      }
    },
    customRequest: async ({ file, onProgress, onSuccess, onError }) => {
      try {
        setFlag(opId, { [loadingKey]: true, [percentKey]: 1 });
        let p = 1;
        const int = setInterval(() => {
          p = Math.min(90, p + 4);
          setFlag(opId, { [percentKey]: p });
          onProgress?.({ percent: p });
        }, 100);

        await toBase64(file);

        clearInterval(int);
        if (isMultiple) {
          setFilesByOp((prev) => {
            const arr = Array.isArray(prev[opId]?.cfdi) ? prev[opId].cfdi : [];
            const nextArr = [...arr, file].slice(0, 3);
            return { ...prev, [opId]: { ...prev[opId], cfdi: nextArr } };
          });
        } else {
          setFile(opId, key, file);
        }

        setFlag(opId, { [percentKey]: 100 });
        onProgress?.({ percent: 100 });
        onSuccess?.('ok');
      } catch (e) {
        onError?.(e);
        notification.error({ message: `Error leyendo ${tipName}` });
      } finally {
        setTimeout(() => setFlag(opId, { [loadingKey]: false }), 200);
      }
    },
  });

  const validateAll = async () => {
    if (!operationIds || operationIds.length === 0) {
      notification.error({ message: 'No hay facturas seleccionadas.' });
      return;
    }
    const missing = [];
    const nameMismatch = [];
    for (let id of operationIds) {
      const f = filesByOp[id];
      if (!f || !f.xml || !f.pdf) missing.push(id);
      else {
        const xmlBase = f.xml.name.split('.')[0];
        const pdfBase = f.pdf.name.split('.')[0];
        if (xmlBase !== pdfBase) nameMismatch.push(id);
      }
    }
    if (missing.length > 0) {
      notification.error({ message: `Faltan XML/PDF en: ${missing.join(', ')}` });
      return;
    }
    if (nameMismatch.length > 0) {
      notification.error({ message: `Nombres no coinciden en: ${nameMismatch.join(', ')}` });
      return;
    }

    setSubmitting(true);
    try {
      const payload = [];
      for (let id of operationIds) {
        const f = filesByOp[id];
        const xmlB64 = (await toBase64(f.xml)).split(',')[1];
        const pdfB64 = (await toBase64(f.pdf)).split(',')[1];

        let cfdiAcuse;
        if (f.cfdi) {
          const fileB64 = (await toBase64(f.cfdi)).split(',')[1];
          cfdiAcuse = {
            extension: (f.cfdi.type && f.cfdi.type.includes('/')) ? f.cfdi.type.split('/')[1] : '',
            contentType: f.cfdi.type || '',
            fileName: f.cfdi.name,
            stringFilecontent: fileB64,
          };
        }

        payload.push({
          operationIdStr: String(id),
          cfdiFileXML: xmlB64,
          cfdiFilePDF: pdfB64,
          validateAr: false,
          ...(cfdiAcuse ? { cfdiAcuse } : {}),
        });
      }

      const res = await validateInvoices(token, payload);
      const ops = res?.result?.operations || [];
      const errs = res?.result?.errors || [];
      setResultOps(ops);
      setResultErrors(errs);
      setShowResults(true);

      if (errs.length) {
        notification.warning({ message: 'Procesado con advertencias' });
      } else {
        notification.success({ message: 'Validación completada.' });
      }
      setReloadOperations(true);
    } catch (err) {
      notification.error({ message: 'Error al validar las facturas.' });
    } finally {
      setSubmitting(false);
    }
  };

  const selectOptions = (operationIds || []).map((id) => {
    const f = filesByOp[id];
    const ready = !!(f && f.xml && f.pdf);
    return {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{`Factura ${id}`}</span>
          {ready && <span style={{ color: '#52c41a' }}>●</span>}
        </div>
      ),
      value: id,
    };
  });

  return (
    <div className="upload-file-form">
      {!showResults && (
        <>
          <Row align="middle" style={{ marginBottom: 12 }}>
            <Col xs={24} lg={10}>
              <Title level={5} type="secondary" style={{ margin: 0 }}>
                Seleccionar factura
              </Title>
            </Col>
            <Col xs={24} lg={14} style={{ textAlign: 'right' }}>
              <Select
                style={{ minWidth: 240 }}
                value={operationIds[currentStep]}
                options={selectOptions}
                onChange={(val) => {
                  const idx = operationIds.indexOf(val);
                  if (idx >= 0) setCurrentStep(idx);
                }}
              />
            </Col>
          </Row>

          <Divider />

          {currentId ? (
            <>
              <Row align="middle">
                <Col xs={24} lg={12}>
                  <Title level={5} type="secondary">
                    Seleccionar XML <Text type="danger">*</Text>
                  </Title>
                </Col>
                <Col
                  xs={24}
                  lg={12}
                  style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}
                >
                  <Spin spinning={!!current.xmlLoading} tip="Cargando XML…">
                    <Upload
                      {...uploadProps(currentId, 'xml', '.xml', 'xmlLoading', 'xmlPercent', 'XML')}
                    >
                      <Button
                        className="upload-file-btn"
                        icon={<UploadOutlined />}
                        loading={!!current.xmlLoading}
                      >
                        Subir XML
                      </Button>
                    </Upload>
                  </Spin>
                </Col>
              </Row>

              <Divider />

              <Row align="middle">
                <Col xs={24} lg={12}>
                  <Title level={5} type="secondary">
                    Seleccionar PDF <Text type="danger">*</Text>
                  </Title>
                </Col>
                <Col
                  xs={24}
                  lg={12}
                  style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}
                >
                  <Spin spinning={!!current.pdfLoading} tip="Cargando PDF…">
                    <Upload
                      {...uploadProps(currentId, 'pdf', '.pdf', 'pdfLoading', 'pdfPercent', 'PDF')}
                    >
                      <Button
                        className="upload-file-btn"
                        icon={<UploadOutlined />}
                        loading={!!current.pdfLoading}
                      >
                        Subir PDF
                      </Button>
                    </Upload>
                  </Spin>
                </Col>
              </Row>

              <Divider />

              <Row align="middle">
                <Col xs={24} lg={12}>
                  <Title level={5} type="secondary">Archivo adicional (opcional)</Title>
                </Col>
                <Col
                  xs={24}
                  lg={12}
                  style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}
                >
                  <Spin spinning={!!current.cfdiLoading} tip="Cargando archivo…">
                    <Upload
                      {...uploadProps(currentId, 'cfdi', '', 'cfdiLoading', 'cfdiPercent', 'archivo', false)}
                    >
                      <Button
                        className="upload-file-btn"
                        icon={<UploadOutlined />}
                        loading={!!current.cfdiLoading}
                      >
                        Subir Archivo
                      </Button>
                    </Upload>
                  </Spin>
                </Col>
              </Row>

              <Divider />

              <Row justify="end">
                <Col>
                  <Space>
                    <Button className="btn-enviar" onClick={validateAll} loading={submitting}>
                      Validar todas
                    </Button>
                  </Space>
                </Col>
              </Row>
            </>
          ) : (
            <div style={{ padding: 20 }}>No hay facturas seleccionadas.</div>
          )}
        </>
      )}

      {showResults && (
        <>
          <Title level={5}>Resultado</Title>
          {resultOps.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={resultOps}
              renderItem={(it) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <strong>Factura:</strong> {it.operationIdStr}
                    <span><strong>Mensaje:</strong> {it.message || '—'}</span>
                  </Space>
                </List.Item>
              )}
            />
          )}
          {resultErrors && resultErrors.length > 0 && (
            <>
              <Divider />
              <Title level={5} type="danger">Errores</Title>
              <List
                size="small"
                bordered
                dataSource={resultErrors}
                renderItem={(e) => (
                  <List.Item>{typeof e === 'string' ? e : JSON.stringify(e)}</List.Item>
                )}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
